import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@munifocus/ui';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { officerIneligibility } from '@munifocus/contracts-gov';
import { useLocalGovernmentOfficers } from '../../lib/local-governments-api';
import { useCertificationCheck } from '../../lib/certifications-api';
import type { FileForm } from './useFileForm';

/**
 * Who works this file.
 *
 * The roster is per local government, so this picker is downstream of the
 * anchor above it: assignment authority is checked against the registry, and an
 * unanchored file has no registry row to check against. Rather than render an
 * empty control, it says which question has to be answered first.
 *
 * Ineligible appointees are shown and disabled, never omitted. The roster
 * deliberately returns staff as well as officers — staff are who a file gets
 * routed to for external action — and an officer looking for a colleague they
 * know is appointed needs to be told *why* the name is not selectable. Silently
 * filtering it reads as the registry being wrong.
 */
export function OfficerPicker({ form, disabled = false }: { form: FileForm; disabled?: boolean }) {
  const { t } = useTranslation();
  const { values, set } = form;
  const roster = useLocalGovernmentOfficers(values.localGovernmentId || null);
  const officers = roster.data ?? [];

  const anchored = values.localGovernmentId !== '';
  const assignee = officers.find((o) => o.userId === values.assignedOfficerId);
  // Advisory only: a municipality that asked for certifications is told when the
  // chosen officer lacks one, and the assignment still goes through. With no
  // requirements set (the default) the check answers `enabled: false` and
  // nothing renders.
  const certCheck = useCertificationCheck(
    anchored ? values.localGovernmentId : null,
    assignee ? assignee.userId : null,
  );
  const missingCerts =
    certCheck.data?.enabled && certCheck.data.officerId === assignee?.userId
      ? certCheck.data.missing
      : [];

  // Re-anchoring is what the server clears an unbacked assignment on. Doing the
  // same here keeps the form honest about what it is going to post, rather than
  // showing a name that will be gone by the time the save comes back.
  useEffect(() => {
    if (values.assignedOfficerId === '') return;
    if (!anchored) {
      set('assignedOfficerId', '');
      return;
    }
    // Only an in-flight or failed roster is a reason to keep it: an empty roster
    // is a definite answer — nobody there can hold this file — and bailing on it
    // left the Select rendering "unassigned" while the form still posted the
    // stale id, which the server then rejected.
    if (roster.isLoading || roster.isError) return;
    if (!officers.some((o) => o.userId === values.assignedOfficerId)) set('assignedOfficerId', '');
  }, [anchored, officers, roster.isError, roster.isLoading, set, values.assignedOfficerId]);

  return (
    <Stack spacing={1}>
      <FormControl fullWidth size="small" disabled={disabled || !anchored || roster.isLoading}>
        <InputLabel>{t('gov.file.assignedOfficer')}</InputLabel>
        <Select
          label={t('gov.file.assignedOfficer')}
          aria-label={t('gov.file.assignedOfficer')}
          value={assignee ? values.assignedOfficerId : ''}
          onChange={(e) => set('assignedOfficerId', e.target.value)}
        >
          {/* An unassigned file sits on the queue, which is a normal state for
              one — most files are picked up rather than routed at intake. */}
          <MenuItem value="">{t('gov.file.assignedOfficerNone')}</MenuItem>
          {officers.map((officer) => {
            const why = officerIneligibility(officer);
            return (
              <MenuItem key={officer.userId} value={officer.userId} disabled={!!why}>
                <Stack>
                  <span>
                    {officer.displayName}
                    {officer.badgeNumber ? ` · ${officer.badgeNumber}` : ''}
                  </span>
                  {why && (
                    <Typography variant="caption" color="text.secondary">
                      {t(`gov.file.officerIneligible.${why}`)}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {!anchored && (
        <Alert severity="info" sx={{ py: 0 }}>
          {t('gov.file.assignedOfficerNeedsAnchor')}
        </Alert>
      )}

      {missingCerts.length > 0 && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('gov.file.certificationWarning.title', { name: assignee?.displayName ?? '' })}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {missingCerts.map((m) => (
              <li key={m.courseId}>
                <Typography variant="body2">
                  {t(`gov.file.certificationWarning.${m.reason}`, {
                    code: m.courseCode,
                    name: m.courseName,
                  })}
                </Typography>
              </li>
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('gov.file.certificationWarning.advisory')}
          </Typography>
        </Alert>
      )}

      {anchored && roster.isError && (
        <Alert severity="error">{t('gov.file.assignedOfficerLoadError')}</Alert>
      )}

      {anchored && !roster.isLoading && !roster.isError && officers.length === 0 && (
        <Alert severity="info" sx={{ py: 0 }}>
          {t('gov.file.assignedOfficerEmptyRoster')}
        </Alert>
      )}

      {/* The whole roster is ineligible — every appointee is staff, or every
          appointment has lapsed. Without this the control reads as broken. */}
      {anchored &&
        officers.length > 0 &&
        officers.every((o) => officerIneligibility(o) !== null) && (
          <Alert severity="warning" sx={{ py: 0 }}>
            {t('gov.file.assignedOfficerNoneEligible')}
          </Alert>
        )}
    </Stack>
  );
}
